import { useState } from 'react';
import { ReturnButton } from '@/components/shared/ReturnButton';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/utils/styles';
import { L4Card } from '@screens/HomePage/MoodMemoryAndFocusPage/L4Card';
import { BadgeSection } from '@screens/HomePage/MoodMemoryAndFocusPage/BadgeSection';
import { DataCardsCarousel } from '../BloodSugarResponsePage/DataCardsCarousel';
import { useParams } from 'react-router-dom';
import {
  useGetNutritionSummaryDetail
} from '@viome/viome-base/src/usecase/Home/HomeQuery';
import { FocusDescription } from '@viome/viome-base/src/usecase/Home/Home.model';
import BoostersBlockersDetailsShimmer from '@/components/skeleton/BoostersBlockersDetails';
import { ErrorLoadingPage } from '@screens/ErrorHandling/ErrorLoadingPage';
import { Render } from '@/components/Helpers/ComponentRender/Render';
import { EventTracker } from '@/utils';

export const MoodMemoryFocusPage = () => {
  const { id } = useParams();

  const {
    data: summaryDetailData,
    isLoading: nutritionDetailsLoading,
    isError: nutritionDetailsError,
  } = useGetNutritionSummaryDetail(Number(id));
  const [showAllL4, setShowAllL4] = useState(false);

  const nutritionSummaryDescriptions: FocusDescription[] =
    summaryDetailData?.my_focus || [];

  const displayedL4Cards = showAllL4
    ? nutritionSummaryDescriptions
    : nutritionSummaryDescriptions.slice(0, 4);
  const displayedBoosters = summaryDetailData?.booster;
  const displayedBlockers = summaryDetailData?.blocker;

  return nutritionDetailsLoading ? (
    <BoostersBlockersDetailsShimmer />
  ) : nutritionDetailsError ? (
    <ErrorLoadingPage />
  ) : (
    <section className="page-container grid grid-cols-1 !py-12">
      <div className="min-h-screen bg-background-secondary space-y-8">
        <ReturnButton />
        <div className="flex items-start justify-between flex-col gap-4">
          <h1 className="typography-d2 text-primary">
            {summaryDetailData?.title}
          </h1>
        </div>

        {(displayedL4Cards?.length > 0) && (
          <>
            <div className="typography-body text-secondary">
              See how your health insights shape your personalized
              recommendations.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {displayedL4Cards?.map((card) => (
                <L4Card
                  key={card.title}
                  title={card.title}
                  description={card.description}
                />
              ))}
            </div>
          </>
        )}

        {nutritionSummaryDescriptions &&
          nutritionSummaryDescriptions?.length > 4 && (
            <button
              onClick={() => {
                EventTracker.trackEvent('home', `boosterBlockers_allGoals`);
                setShowAllL4(!showAllL4);
              }}
              className={cn(
                'w-full py-4 rounded-xl',
                'bg-white',
                'flex items-center justify-center gap-2',
                'typography-body text-primary',
              )}
            >
              {showAllL4 ? 'Show less' : 'Show all'}
              <ChevronDown
                className={cn(
                  'w-5 h-5 transition-transform',
                  showAllL4 && 'rotate-180',
                )}
              />
            </button>
          )}
        <Render
          when={
            summaryDetailData?.booster && summaryDetailData?.booster.length > 0
          }
        >
          <div className="space-y-6">
            <div className="space-y-2">
              <h2 className="typography-h2 text-primary">My Boosters</h2>
              <p className="typography-body text-secondary">
                Explore your "boosters", the ones that drive improvement in your
                health journey.
              </p>
            </div>

            {displayedBoosters && (
              <div className="space-y-6">
                <BadgeSection badgeItems={displayedBoosters} influencer='booster' />
              </div>
            )}
          </div>
        </Render>

        <Render
          when={
            summaryDetailData?.blocker && summaryDetailData?.blocker.length > 0
          }
        >
          <div className="space-y-6">
            <div className="space-y-2">
              <h2 className="typography-h2 text-primary">My Blockers</h2>
              <p className="typography-body text-secondary">
                Meet your "blockers", the ones that may hinder progress in your
                health journey.
              </p>
            </div>

            {displayedBlockers && (
              <div className="space-y-6">
                <BadgeSection badgeItems={displayedBlockers} influencer='blocker' />
              </div>
            )}
          </div>
        </Render>

        <DataCardsCarousel scoreId={Number(id)} />
      </div>
    </section>
  );
};
